import { useState, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityRouteParams,
  entityRouteRef,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { useNavigate } from 'react-router-dom';
import {
  DependencyGraph,
  DependencyGraphTypes,
  Progress,
} from '@backstage/core-components';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Checkbox from '@material-ui/icons/CheckBox';
import CheckboxBlank from '@material-ui/icons/CheckBoxOutlineBlank';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import useAsync from 'react-use/lib/useAsync';
import { Project } from '@internal/plugin-pulse-common';

// ── Node types ────────────────────────────────────────────────────────────────

type NodeKind = 'project' | 'tag' | 'technology';
type NodeData = {
  kind: NodeKind;
  label: string;
  /** Populated for tag nodes — whether children are currently shown */
  expanded?: boolean;
  /** Populated for tag nodes — call to toggle expansion */
  onToggle?: () => void;
  /** Populated for technology nodes — the entity ref string */
  entityRef?: string;
};
type EdgeData = object;

// ── Custom SVG node renderer ──────────────────────────────────────────────────

function GraphNode({ node }: DependencyGraphTypes.RenderNodeProps<NodeData>) {
  const theme = useTheme();
  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);
  const textRef = useRef<SVGTextElement>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (textRef.current) {
      const { width: bw, height: bh } = textRef.current.getBBox();
      const rw = Math.round(bw);
      const rh = Math.round(bh);
      if (rw !== width || rh !== height) {
        setWidth(rw);
        setHeight(rh);
      }
    }
  }, [width, height]);

  const padding = 10;
  const rw = width + padding * 2;
  const rh = height + padding * 2;

  const isTag = node.kind === 'tag';
  const isTechnology = node.kind === 'technology';

  const handleTechClick =
    isTechnology && node.entityRef
      ? () => {
          const params = entityRouteParams(node.entityRef!);
          navigate(entityRoute(params));
        }
      : undefined;

  let fill: string;
  if (node.kind === 'project') fill = theme.palette.primary.main;
  else if (isTag) fill = theme.palette.secondary.main;
  else fill = theme.palette.primary.light;

  let textFill: string;
  if (node.kind === 'project') textFill = theme.palette.primary.contrastText;
  else if (isTag) textFill = theme.palette.secondary.contrastText;
  else textFill = theme.palette.primary.contrastText;

  const rx = isTag ? 14 : 6;

  // For tag nodes append a directional indicator to the visible label
  const displayLabel = isTag
    ? `${node.label} ${node.expanded ? '▾' : '▸'}`
    : node.label;

  const clickHandler = isTag ? node.onToggle : handleTechClick;

  return (
    <g
      onClick={clickHandler}
      style={clickHandler ? { cursor: 'pointer' } : undefined}
    >
      <rect width={rw} height={rh} rx={rx} fill={fill} stroke={fill} />
      <text
        ref={textRef}
        x={rw / 2}
        y={rh / 2}
        dominantBaseline="middle"
        textAnchor="middle"
        fill={textFill}
        fontSize={12}
        fontWeight={node.kind === 'project' ? 700 : 500}
        fontFamily={theme.typography.fontFamily}
        style={clickHandler ? { userSelect: 'none' } : undefined}
      >
        {displayLabel}
      </text>
    </g>
  );
}

// ── Graph data builder ────────────────────────────────────────────────────────

type TechInfo = { name: string; ref: string; tags: string[] };

function buildGraphData(
  projectLabel: string,
  techs: TechInfo[],
  selectedTags: string[],
  expandedTags: Set<string>,
  onToggleTag: (tag: string) => void,
): {
  nodes: DependencyGraphTypes.DependencyNode<NodeData>[];
  edges: DependencyGraphTypes.DependencyEdge<EdgeData>[];
} {
  const projectId = 'project:root';
  const nodes: DependencyGraphTypes.DependencyNode<NodeData>[] = [
    { id: projectId, kind: 'project', label: projectLabel },
  ];
  const edges: DependencyGraphTypes.DependencyEdge<EdgeData>[] = [];

  // Group techs by tag, respecting the active filter
  const tagMap = new Map<string, TechInfo[]>();
  for (const tech of techs) {
    const effectiveTags = tech.tags.length ? tech.tags : ['Uncategorized'];
    const visibleTags = selectedTags.length
      ? effectiveTags.filter(t => selectedTags.includes(t))
      : effectiveTags;
    for (const tag of visibleTags) {
      if (!tagMap.has(tag)) tagMap.set(tag, []);
      tagMap.get(tag)!.push(tech);
    }
  }

  // Sort tags alphabetically, Uncategorized last
  const sortedTags = [...tagMap.keys()].sort((a, b) => {
    if (a === 'Uncategorized') return 1;
    if (b === 'Uncategorized') return -1;
    return a.localeCompare(b);
  });

  for (const tag of sortedTags) {
    const tagId = `tag:${tag}`;
    const isExpanded = expandedTags.has(tag);

    nodes.push({
      id: tagId,
      kind: 'tag',
      label: tag,
      expanded: isExpanded,
      onToggle: () => onToggleTag(tag),
    });
    edges.push({ from: projectId, to: tagId });

    // Only emit technology child nodes when this tag is expanded
    if (isExpanded) {
      for (const tech of tagMap.get(tag)!) {
        // Tag-scoped ID so the same technology can appear under multiple tags
        const techId = `tech:${tech.ref}:${tag}`;
        nodes.push({
          id: techId,
          kind: 'technology',
          label: tech.name,
          entityRef: tech.ref,
        });
        edges.push({ from: tagId, to: techId });
      }
    }
  }

  return { nodes, edges };
}

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'visible',
  },
  toolbar: {
    padding: theme.spacing(1.5, 2, 0),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  formControl: {
    minWidth: 240,
  },
  cardContent: {
    padding: 0,
    overflow: 'visible',
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
  },
  dialogTitleControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  dialogContent: {
    padding: theme.spacing(0, 2, 2),
    overflowX: 'auto',
    overflowY: 'auto',
  },
  scrollContainer: {
    overflowX: 'auto',
    overflowY: 'auto',
    width: '100%',
  },
  emptyState: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

// ── Main component ────────────────────────────────────────────────────────────

export function TechnologyRelationsGraphCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set());
  const [fullscreen, setFullscreen] = useState(false);

  const relatedTechnologies =
    (entity.spec as Project)?.relatedTechnologies ?? [];
  const refs = relatedTechnologies.map(t => t.ref);
  const refsKey = refs.join(',');

  // Fetch technology entities to resolve names + tags
  const {
    value: techs = [],
    loading,
    error,
  } = useAsync(async (): Promise<TechInfo[]> => {
    if (!refs.length) return [];

    const result = await catalogApi.getEntitiesByRefs({
      entityRefs: refs,
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'spec.tags',
      ],
    });

    return (result.items ?? [])
      .map((item, idx) => {
        const e =
          item && typeof item === 'object' && 'entity' in item
            ? (item as any).entity
            : item;
        if (!e?.metadata) return null;
        return {
          name: e.metadata.title || e.metadata.name || refs[idx] || 'Unknown',
          ref: refs[idx] ?? '',
          tags: Array.isArray(e.spec?.tags)
            ? (e.spec.tags as string[]).map(String)
            : [],
        } satisfies TechInfo;
      })
      .filter((t): t is TechInfo => t !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, refsKey]);

  // All unique tags across the project's technologies (for dropdown)
  const availableTags = useMemo(() => {
    const set = new Set<string>();
    for (const tech of techs) {
      for (const tag of tech.tags) set.add(tag);
    }
    return [...set].sort((a, b) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });
  }, [techs]);

  // Tags visible in the current graph (after filter)
  const visibleTags = useMemo(
    () => (selectedTags.length ? selectedTags : availableTags),
    [selectedTags, availableTags],
  );

  const allExpanded =
    visibleTags.length > 0 && visibleTags.every(t => expandedTags.has(t));

  const handleToggleTag = useCallback((tag: string) => {
    setExpandedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const handleExpandCollapseAll = useCallback(() => {
    setExpandedTags(allExpanded ? new Set() : new Set(visibleTags));
  }, [allExpanded, visibleTags]);

  const { nodes, edges } = useMemo(
    () =>
      buildGraphData(
        entity.metadata.title || entity.metadata.name,
        techs,
        selectedTags,
        expandedTags,
        handleToggleTag,
      ),
    // handleToggleTag is stable; expandedTags identity changes on each toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entity, techs, selectedTags, expandedTags],
  );

  // ── Shared toolbar controls ─────────────────────────────────────────────────

  const tagFilter = availableTags.length > 0 && (
    <FormControl
      variant="outlined"
      size="small"
      className={classes.formControl}
    >
      <InputLabel id="tech-tag-filter-label">Filter by tag</InputLabel>
      <Select
        labelId="tech-tag-filter-label"
        multiple
        value={selectedTags}
        onChange={e => {
          setSelectedTags(e.target.value as string[]);
          // Clear expansion when filter changes so state stays coherent
          setExpandedTags(new Set());
        }}
        label="Filter by tag"
        renderValue={selected => (
          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {(selected as string[]).map(tag => (
              <Chip key={tag} label={tag} size="small" />
            ))}
          </Box>
        )}
      >
        {availableTags.map(tag => (
          <MenuItem key={tag} value={tag}>
            <ListItemIcon style={{ minWidth: 32 }}>
              {selectedTags.includes(tag) ? (
                <Checkbox fontSize="small" />
              ) : (
                <CheckboxBlank fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText primary={tag} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const expandAllButton = visibleTags.length > 0 && (
    <Button size="small" variant="outlined" onClick={handleExpandCollapseAll}>
      {allExpanded ? 'Collapse all' : 'Expand all'}
    </Button>
  );

  // ── Graph ───────────────────────────────────────────────────────────────────

  const graph =
    nodes.length <= 1 ? (
      <Typography className={classes.emptyState}>
        No related technologies
      </Typography>
    ) : (
      <DependencyGraph<NodeData, EdgeData>
        nodes={nodes}
        edges={edges}
        renderNode={GraphNode}
        direction={DependencyGraphTypes.Direction.LEFT_RIGHT}
        zoom="enable-on-click"
        showArrowHeads
        allowFullscreen={false}
        nodeMargin={20}
        rankMargin={40}
        paddingX={32}
        paddingY={32}
      />
    );

  return (
    <>
      <Card className={classes.card}>
        <CardHeader
          title="Technology Relations"
          action={
            nodes.length > 1 && (
              <Tooltip title="Expand to fullscreen">
                <IconButton
                  size="small"
                  onClick={() => setFullscreen(true)}
                  aria-label="Expand technology relations to fullscreen"
                >
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            )
          }
        />

        {loading && <Progress />}

        {!loading && error && (
          <Alert severity="error">
            Failed to load technologies: {error.message}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {(tagFilter || expandAllButton) && (
              <Box className={classes.toolbar}>
                {tagFilter}
                {expandAllButton}
              </Box>
            )}
            <CardContent className={classes.cardContent}>{graph}</CardContent>
          </>
        )}
      </Card>

      <Dialog
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        fullWidth
        maxWidth="xl"
        aria-labelledby="tech-relations-fullscreen-title"
      >
        <DialogTitle
          id="tech-relations-fullscreen-title"
          disableTypography
          className={classes.dialogTitle}
        >
          <Typography variant="h6">Technology Relations</Typography>
          <Box className={classes.dialogTitleControls}>
            {tagFilter}
            {expandAllButton}
            <Tooltip title="Exit fullscreen">
              <IconButton
                size="small"
                onClick={() => setFullscreen(false)}
                aria-label="Exit fullscreen"
              >
                <FullscreenExitIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={() => setFullscreen(false)}
                aria-label="Close dialog"
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.scrollContainer}>{graph}</Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
